<agr-page-detail
  [pageTitle]="pageTitle$ | async"
  [hasPrimaryAction]="true"
  primaryActionIcon="print"
  [primaryActionText]="'printReport' | translate"
  (primaryAction)="print()"
  (backAction)="back()"
  [buttonItems]="buttonItems$ | async"
  (buttonAction)="buttonAction($event)"
>
  <agr-print-page
    [footerText]="'fertilizationWarningFooter' | translate"
    [relatedPeople]="relatedPeople$ | async"
    [marginLeft]="true"
    [showPageNumber]="false"
  >
    <app-general-info></app-general-info>

    @if (legalStandardBalance$ | async) {
      <div class="fertilization-legal-standards-table wrapper--spacing-horizontal text--body">
        <h2 class="text--h3">{{ 'legalStandards' | translate }}</h2>
        <div class="avoid-page-break spacing--margin-top">
          <div class="flex--row print-border-all background-color--gray4">
            <div
              class="flex__child flex__child-3 table-header print-padding align-desktop-top align-print-center"
            >
              {{ 'mainCultivationsInKgHa' | translate }}
            </div>
            <div
              class="flex__child flex__child-1 table-header text--center print-padding-vertical align-desktop-top align-print-center"
            >
              {{ 'hectares' | translate }}
            </div>
            <div class="flex__child flex__child-4">
              <div class="flex--row">
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'nFertilizer' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'phosphate' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'nTotal' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          @for (mainCropField of sortedMainCropFields$ | async; track mainCropField) {
            <div
              class="flex--row print-border-no-top"
              data-test="fertilization-legal-standards-mainCropField"
            >
              <div
                class="flex__child flex__child-3 flex print-padding-horizontal align-desktop-top align-print-center"
                data-test="fertilization-legal-standards-mainCropField__name"
              >
                {{ mainCropField.cropFieldName }}, {{ mainCropField.cropName }}
                @if (mainCropField?.cropVarietyName) {
                  <span>&nbsp;({{ mainCropField?.cropVarietyName }})</span>
                }
                @if (mainCropField?.grasslandUsage) {
                  <span>:&nbsp;{{ mainCropField?.grasslandUsage.toLowerCase() | translate }}</span>
                }
                @if (mainCropField?.isPerennialCultivation) {
                  <span>&nbsp;-&nbsp;{{ 'perennialCultivation' | translate }}</span>
                }
                @if (mainCropField?.nitrogenCalculationSettings?.isAfterTornGrass) {
                  <span> &nbsp;({{ 'afterTornGrassShort' | translate }}) </span>
                }
              </div>
              <div
                class="flex__child flex__child-1 text--center align-desktop-top align-print-center"
                data-test="fertilization-legal-standards-mainCropField__hectares"
              >
                {{ mainCropField.areaInHectares | agrNumber: '1.2-2' }}
              </div>
              <div class="flex__child flex__child-4">
                <div class="flex--row">
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-mainCropField__nManureLegalStandard"
                    >
                      {{ mainCropField.nManureLegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-mainCropField__nManureUsage"
                    >
                      {{ mainCropField.nManureUsage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-mainCropField__p2O5LegalStandard"
                    >
                      {{ mainCropField.p2O5LegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-mainCropField__p2O5Usage"
                    >
                      {{ mainCropField.p2O5Usage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-mainCropField__nTotalLegalStandard"
                    >
                      {{ mainCropField.nTotalLegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-mainCropField__nTotalUse"
                    >
                      {{ mainCropField.nTotalUsage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
        <div class="spacing--margin-vertical"><mat-divider></mat-divider></div>
        <div class="avoid-page-break">
          <div class="flex--row print-border-all background-color--gray4">
            <div
              class="flex__child flex__child-3 table-header print-padding align-desktop-top align-print-center"
            >
              {{ 'subsequentCultivationsInKgHa' | translate }}
            </div>
            <div
              class="flex__child flex__child-1 table-header text--center print-padding-vertical align-desktop-top align-print-center"
            >
              {{ 'hectares' | translate }}
            </div>
            <div class="flex__child flex__child-4">
              <div class="flex--row">
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'nFertilizer' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'phosphate' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'nTotal' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          @for (
            subsequentCultivation of sortedSubsequentCultivations$ | async;
            track subsequentCultivation
          ) {
            <div
              class="flex--row print-border-no-top"
              data-test="fertilization-legal-standards-subsequentCultivation"
            >
              <div
                class="flex__child flex__child-3 flex print-padding-horizontal"
                style="align-self: center"
                data-test="fertilization-legal-standards-subsequentCultivation__name"
              >
                {{ subsequentCultivation.cropFieldName }}, {{ subsequentCultivation.cropName }}
                @if (subsequentCultivation?.cropVarietyName) {
                  <span>&nbsp;({{ subsequentCultivation?.cropVarietyName }})</span>
                }
              </div>
              <div
                class="flex__child flex__child-1 text--center"
                style="align-self: center"
                data-test="fertilization-legal-standards-subsequentCultivation__hectares"
              >
                {{ subsequentCultivation.areaInHectares | agrNumber: '1.2-2' }}
              </div>
              <div class="flex__child flex__child-4">
                <div class="flex--row">
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-subsequentCultivation__nManureLegalStandard"
                    >
                      {{ subsequentCultivation.nManureLegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-subsequentCultivation__nManureUsage"
                    >
                      {{ subsequentCultivation.nManureUsage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-subsequentCultivation__p2O5LegalStandard"
                    >
                      {{ subsequentCultivation.p2O5LegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-subsequentCultivation__p2O5Usage"
                    >
                      {{ subsequentCultivation.p2O5Usage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-subsequentCultivation__nTotalLegalStandard"
                    >
                      {{ subsequentCultivation.nTotalLegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-subsequentCultivation__nTotalUsage"
                    >
                      {{ subsequentCultivation.nTotalUsage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
        <div class="spacing--margin-vertical"><mat-divider></mat-divider></div>
        @if ((legalStandardBalance$ | async)?.nitrogenCuts.cutsTotalInKgN > 0) {
          <div class="avoid-page-break">
            <div class="flex--row print-border-all background-color--gray4">
              <div
                class="flex__child flex__child-3 table-header print-padding align-desktop-top align-print-center"
              >
                {{ 'nitrogenCuts.sectionTitle' | translate }}
              </div>
              <div
                class="flex__child flex__child-1 table-header text--center print-padding-vertical align-desktop-top align-print-center"
              >
                {{ 'hectares' | translate }}
              </div>
              <div class="flex__child flex__child-4">
                <div class="flex--row">
                  <div
                    class="flex__child flex__child-1 table-header text--center print-border-left"
                  ></div>
                  <div
                    class="flex__child flex__child-1 table-header text--center print-border-left"
                  ></div>
                  <div
                    class="flex__child flex__child-1 table-header text--center print-border-left"
                  >
                    <div class="print-border-bottom print-padding-vertical">
                      {{ 'nTotal' | translate }}
                    </div>
                    <div class="flex--row flex--nowrap">
                      <div class="flex__child flex__child-1 text--center text--label print-padding">
                        {{ 'standard' | translate }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ng-container *ngrxLet="cropYear$ as cropYear">
              <ng-container *ngrxLet="legalStandardBalance$ as legalStandardBalance">
                @if (legalStandardBalance) {
                  <div
                    class="flex--row print-border-no-top nitrogen-cuts-container"
                    data-test="fertilization-legal-standards-nitrogenCuts"
                  >
                    <div
                      class="flex__child flex__child-3 flex print-padding-horizontal"
                      style="align-self: center"
                      data-test="fertilization-legal-standards-nitrogenCut__name"
                    >
                      @if (cropYear !== 2025) {
                        {{ 'nitrogenCuts.period1Label' | translate }}
                      } @else {
                        {{ 'nitrogenCuts.period1Label2025' | translate }}
                      }
                    </div>
                    @if (legalStandardBalance.nitrogenCuts) {
                      <div
                        class="flex__child flex__child-1 text--center"
                        style="align-self: center"
                        data-test="fertilization-legal-standards-nitrogenCuts__hectares"
                      >
                        {{ legalStandardBalance.nitrogenCuts.period1Ha | agrNumber: '1.2-2' }}
                      </div>
                    }
                    <div class="flex__child flex__child-4">
                      <div class="flex--row">
                        <div
                          class="flex__child flex__child-1 flex--row text--center print-border-left"
                        >
                          <div class="flex__child flex__child-1 text--center print-padding"></div>
                          <div
                            class="flex__child flex__child-1 text--center print-padding print-border-left"
                          ></div>
                        </div>
                        <div
                          class="flex__child flex__child-1 flex--row text--center print-border-left"
                        >
                          <div class="flex__child flex__child-1 text--center print-padding"></div>
                          <div
                            class="flex__child flex__child-1 text--center print-padding print-border-left"
                          ></div>
                        </div>
                        <div
                          class="flex__child flex__child-1 flex--row text--center print-border-left"
                        >
                          <div
                            class="flex__child flex__child-1 text--center print-padding"
                            data-test="fertilization-legal-standards-subsequentCultivation__nTotalLegalStandard"
                          >
                            -5
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                <div
                  class="flex--row print-border-no-top nitrogen-cuts-container"
                  data-test="fertilization-legal-standards-nitrogenCuts"
                >
                  <div
                    class="flex__child flex__child-3 flex print-padding-horizontal"
                    style="align-self: center"
                    data-test="fertilization-legal-standards-nitrogenCut__name"
                  >
                    @if (cropYear !== 2025) {
                      {{ 'nitrogenCuts.period2Label' | translate }}
                    } @else {
                      {{ 'nitrogenCuts.period2Label2025' | translate }}
                    }
                  </div>
                  @if (legalStandardBalance.nitrogenCuts) {
                    <div
                      class="flex__child flex__child-1 text--center"
                      style="align-self: center"
                      data-test="fertilization-legal-standards-nitrogenCuts__hectares"
                    >
                      {{ legalStandardBalance.nitrogenCuts.period2Ha | agrNumber: '1.2-2' }}
                    </div>
                  }
                  <div class="flex__child flex__child-4">
                    <div class="flex--row">
                      <div
                        class="flex__child flex__child-1 flex--row text--center print-border-left"
                      >
                        <div class="flex__child flex__child-1 text--center print-padding"></div>
                        <div
                          class="flex__child flex__child-1 text--center print-padding print-border-left"
                        ></div>
                      </div>
                      <div
                        class="flex__child flex__child-1 flex--row text--center print-border-left"
                      >
                        <div class="flex__child flex__child-1 text--center print-padding"></div>
                        <div
                          class="flex__child flex__child-1 text--center print-padding print-border-left"
                        ></div>
                      </div>
                      <div
                        class="flex__child flex__child-1 flex--row text--center print-border-left"
                      >
                        <div
                          class="flex__child flex__child-1 text--center print-padding"
                          data-test="fertilization-legal-standards-subsequentCultivation__nTotalLegalStandard"
                        >
                          -10
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="flex--row print-border-no-top nitrogen-cuts-container"
                  data-test="fertilization-legal-standards-nitrogenCuts"
                >
                  <div
                    class="flex__child flex__child-3 flex print-padding-horizontal"
                    style="align-self: center"
                    data-test="fertilization-legal-standards-nitrogenCut__name"
                  >
                    @if (cropYear !== 2025) {
                      {{ 'nitrogenCuts.period3Label' | translate }}
                    } @else {
                      {{ 'nitrogenCuts.period3Label2025' | translate }}
                    }
                  </div>
                  @if (legalStandardBalance.nitrogenCuts) {
                    <div
                      class="flex__child flex__child-1 text--center"
                      style="align-self: center"
                      data-test="fertilization-legal-standards-nitrogenCuts__hectares"
                    >
                      {{ legalStandardBalance.nitrogenCuts.period3Ha | agrNumber: '1.2-2' }}
                    </div>
                  }
                  <div class="flex__child flex__child-4">
                    <div class="flex--row">
                      <div
                        class="flex__child flex__child-1 flex--row text--center print-border-left"
                      >
                        <div class="flex__child flex__child-1 text--center print-padding"></div>
                        <div
                          class="flex__child flex__child-1 text--center print-padding print-border-left"
                        ></div>
                      </div>
                      <div
                        class="flex__child flex__child-1 flex--row text--center print-border-left"
                      >
                        <div class="flex__child flex__child-1 text--center print-padding"></div>
                        <div
                          class="flex__child flex__child-1 text--center print-padding print-border-left"
                        ></div>
                      </div>
                      <div
                        class="flex__child flex__child-1 flex--row text--center print-border-left"
                      >
                        <div
                          class="flex__child flex__child-1 text--center print-padding"
                          data-test="fertilization-legal-standards-subsequentCultivation__nTotalLegalStandard"
                        >
                          -20
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </ng-container>
          </div>
        }
        <div class="spacing--margin-vertical"><mat-divider></mat-divider></div>
        <div class="avoid-page-break">
          <div class="flex--row print-border-all background-color--gray4">
            <div
              class="flex__child flex__child-3 table-header print-padding align-desktop-top align-print-center"
            >
              {{ 'usageStandardsTotalInKg' | translate }}
            </div>
            <div
              class="flex__child flex__child-1 table-header text--center print-padding-vertical align-desktop-top align-print-center"
            >
              {{ 'hectares' | translate }}
            </div>
            <div class="flex__child flex__child-4">
              <div class="flex--row">
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'nFertilizer' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'phosphate' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
                <div class="flex__child flex__child-1 table-header text--center print-border-left">
                  <div class="print-border-bottom print-padding-vertical">
                    {{ 'nTotal' | translate }}
                  </div>
                  <div class="flex--row flex--nowrap">
                    <div class="flex__child flex__child-1 text--center text--label print-padding">
                      {{ 'standard' | translate }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center text--label print-padding print-border-left"
                    >
                      {{ 'usage' | translate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="flex--row print-border-no-top"
            *ngrxLet="legalStandardBalance$ as legalStandardBalance"
            data-test="fertilization-legal-standards-balance"
          >
            <div
              class="flex__child flex__child-3 table-header print-padding-horizontal"
              style="align-self: center"
            >
              {{ 'total' | translate }}
            </div>
            @if (legalStandardBalance) {
              <div
                class="flex__child flex__child-1 text--center"
                style="align-self: center"
                data-test="fertilization-legal-standards-balance__hectares"
              >
                {{ legalStandardBalance.areaInHectares | agrNumber: '1.2-2' }}
              </div>
              <div class="flex__child flex__child-4">
                <div class="flex--row">
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-balance__nManureLegalStandard"
                    >
                      {{ legalStandardBalance.nManureLegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-balance__nManureUsage"
                    >
                      {{ legalStandardBalance.nManureUsage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-balance__p2O5LegalStandard"
                    >
                      {{ legalStandardBalance.p2O5LegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-balance__p2O5Usage"
                    >
                      {{ legalStandardBalance.p2O5Usage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 flex--row text--center print-border-left">
                    <div
                      class="flex__child flex__child-1 text--center print-padding"
                      data-test="fertilization-legal-standards-balance__nTotalLegalStanard"
                    >
                      {{ legalStandardBalance.nTotalLegalStandard | agrNumber: '1.0-0' }}
                    </div>
                    <div
                      class="flex__child flex__child-1 text--center print-padding print-border-left"
                      data-test="fertilization-legal-standards-balance__nTotalUsage"
                    >
                      {{ legalStandardBalance.nTotalUsage | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                </div>
                <div class="flex--row print-border-top">
                  <div class="flex__child flex__child-1 text--center print-border-left">
                    <div class="print-border-bottom print-padding-vertical text--label">
                      <strong>{{ 'balance' | translate }}</strong>
                    </div>
                    <div
                      class="print-padding-vertical"
                      data-test="fertilization-legal-standards-balance__nManureBalance"
                      [ngClass]="{
                        'background-color--success-200': hasSuccessBg(
                          legalStandardBalance.nManureBalance
                        ),
                        'background-color--warn-200': hasWarnBg(
                          legalStandardBalance.nManureBalance
                        ),
                      }"
                    >
                      {{ legalStandardBalance.nManureBalance | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 text--center print-border-left">
                    <div class="print-border-bottom print-padding-vertical text--label">
                      <strong>{{ 'balance' | translate }}</strong>
                    </div>
                    <div
                      class="print-padding-vertical"
                      data-test="fertilization-legal-standards-balance__p2O5Balance"
                      [ngClass]="{
                        'background-color--success-200': hasSuccessBg(
                          legalStandardBalance.p2O5Balance
                        ),
                        'background-color--warn-200': hasWarnBg(legalStandardBalance.p2O5Balance),
                      }"
                    >
                      {{ legalStandardBalance.p2O5Balance | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                  <div class="flex__child flex__child-1 text--center print-border-left">
                    <div class="print-border-bottom print-padding-vertical text--label">
                      <strong>{{ 'balance' | translate }}</strong>
                    </div>
                    <div
                      class="print-padding-vertical"
                      data-test="fertilization-legal-standards-balance__nTotalBalance"
                      [ngClass]="{
                        'background-color--success-200': hasSuccessBg(
                          legalStandardBalance.nTotalBalance
                        ),
                        'background-color--warn-200': hasWarnBg(legalStandardBalance.nTotalBalance),
                      }"
                    >
                      {{ legalStandardBalance.nTotalBalance | agrNumber: '1.0-0' }}
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
          <div>
            <br />
          </div>
        </div>
      </div>
    }
  </agr-print-page>
</agr-page-detail>
